import { errorMessage } from "@streamoid/errors";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { notify } from "../../utils/utils";
import { getCleanupReportData } from "../../utils/dataCleanup";
import { CENTRALISED_BILLING_API } from "../../utils/envConfig";
import { downloadFile, isoDate } from "./csv";
import { buildReport, ReportRow } from "./reportRules";

/*
 * The report: every workspace and every user, with what the cleanup rules
 * decide (DELETE or KEEP) and why — kept ones too, which the other tabs never
 * show. The backend gives the database side, billing is read here, and the
 * rules are applied here (reportRules.ts); nothing is saved. The table and
 * the Excel file are built from the same rows and the same columns, so the
 * file is exactly what is on screen, every page of it.
 */

const PAGE_SIZE = 50;

interface Report {
  generatedAt: Date;
  rows: ReportRow[];
}

// The cleanup list's statuses, as the tabs name them.
const LIST_STATUS: Record<string, string> = {
  watching: "To review",
  excluded: "Kept",
  deleted: "Deleted",
  restored: "Restored",
};

type Value = string | number | Date | null;

/**
 * The columns of the table and of the sheet, in order — the report sheet's
 * (workspaceId … reason), with type and action first. For a user row the
 * owner columns are the user. `width` is the sheet's column width.
 */
const COLUMNS: {
  header: string;
  get: (r: ReportRow) => Value;
  width: number;
}[] = [
  { header: "type", get: (r) => r.type, width: 11 },
  { header: "action", get: (r) => r.action, width: 9 },
  { header: "workspaceId", get: (r) => r.workspaceId ?? "", width: 26 },
  { header: "workspaceName", get: (r) => r.workspaceName ?? "", width: 30 },
  { header: "ownerId", get: (r) => r.ownerId, width: 26 },
  { header: "ownerName", get: (r) => r.ownerName ?? "", width: 22 },
  { header: "ownerEmail", get: (r) => r.ownerEmail ?? "", width: 32 },
  {
    header: "ownerWorkspaces",
    get: (r) => r.ownerWorkspaces ?? null,
    width: 16,
  },
  { header: "planType", get: (r) => r.planType ?? "", width: 10 },
  { header: "planName", get: (r) => r.planName ?? "", width: 18 },
  { header: "createdAt", get: (r) => r.createdAt, width: 12 },
  { header: "lastActive", get: (r) => r.lastActive, width: 12 },
  { header: "reason", get: (r) => r.reason, width: 70 },
  {
    header: "onCleanupList",
    get: (r) => LIST_STATUS[r.listStatus || ""] ?? r.listStatus ?? "",
    width: 14,
  },
];

/** A value as the table shows it — and as the sheet's date format shows it. */
const show = (value: Value) =>
  value instanceof Date ? isoDate(value.toISOString()) : (value ?? "");

/**
 * Excel's filter buttons on the header row. The sheet's XML holds
 * <autoFilter/> right after <sheetData/>, as the file format orders it.
 */
const headerFilter = (lastRow: number) => ({
  files: {
    transform: {
      "xl/worksheets/sheet{id}.xml": {
        transform: (content: string) =>
          content.replace(
            "</sheetData>",
            `</sheetData><autoFilter ref="A1:${String.fromCharCode(64 + COLUMNS.length)}${lastRow}"/>`,
          ),
      },
    },
  },
});

// Workspaces before users, DELETE before KEEP, then by owner.
const order = (a: ReportRow, b: ReportRow) =>
  (a.type === b.type ? 0 : a.type === "workspace" ? -1 : 1) ||
  a.action.localeCompare(b.action) ||
  (a.ownerEmail || "").localeCompare(b.ownerEmail || "") ||
  (a.workspaceName || "").localeCompare(b.workspaceName || "");

const matches = (r: ReportRow, text: string) =>
  [r.workspaceId, r.workspaceName, r.ownerId, r.ownerName, r.ownerEmail].some(
    (v) => v && v.toLowerCase().includes(text),
  );

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/**
 * Stays mounted while other tabs are open, so a report is not worked out
 * again just for switching tabs. `active`: this tab is showing; the first
 * time it is, the report is asked for.
 */
export default function FullReport({ active }: { active: boolean }) {
  const [report, setReport] = useState<Report | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState("");
  const [isDownloading, setIsDownloading] = useState(false);
  const [type, setType] = useState("");
  const [action, setAction] = useState("");
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const request = useRef(0);
  const asked = useRef(false);

  const load = () => {
    const current = ++request.current;
    setIsLoading(true);
    setError("");
    setProgress({ done: 0, total: 0 });
    getCleanupReportData()
      .then((res) =>
        buildReport(res.data, {
          billing: CENTRALISED_BILLING_API,
          onProgress: (done, total) => {
            if (current === request.current) setProgress({ done, total });
          },
        }),
      )
      .then((rows) => {
        if (current !== request.current) return;
        setReport({ generatedAt: new Date(), rows: rows.sort(order) });
        setOffset(0);
      })
      .catch((err) => {
        if (current !== request.current) return;
        setError(errorMessage(err));
      })
      .finally(() => {
        if (current === request.current) setIsLoading(false);
      });
  };

  useEffect(() => {
    if (active && !asked.current) {
      asked.current = true;
      load();
    }
  }, [active]);

  // What the filters let through: shown page by page, downloaded whole.
  const rows = useMemo(() => {
    const text = search.trim().toLowerCase();
    return (report?.rows || []).filter(
      (r) =>
        (!type || r.type === type) &&
        (!action || r.action === action) &&
        (!text || matches(r, text)),
    );
  }, [report, type, action, search]);

  const counts = useMemo(() => {
    const c = {
      workspace: { DELETE: 0, KEEP: 0 },
      user: { DELETE: 0, KEEP: 0 },
    };
    for (const r of report?.rows || []) c[r.type][r.action]++;
    return c;
  }, [report]);

  const changeFilter = (apply: () => void) => {
    setOffset(0);
    apply();
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      // Loaded only when needed: it is not part of the page itself.
      const { default: writeXlsxFile } =
        await import("write-excel-file/browser");
      const sheetData = [
        COLUMNS.map((c) => ({ value: c.header, fontWeight: "bold" as const })),
        ...rows.map((r) => COLUMNS.map((c) => c.get(r))),
      ];
      const blob = await writeXlsxFile(
        sheetData,
        {
          sheet: "Report",
          columns: COLUMNS.map((c) => ({ width: c.width })),
          stickyRowsCount: 1,
          dateFormat: "yyyy-mm-dd",
        },
        { features: [headerFilter(rows.length + 1)] },
      ).toBlob();
      const name = [
        "cleanup-report",
        isoDate(report!.generatedAt.toISOString()),
        type && `${type}s`,
        action.toLowerCase(),
        search.trim() && "search",
      ]
        .filter(Boolean)
        .join("-");
      downloadFile(`${name}.xlsx`, blob);
      notify(`Downloaded ${rows.length} rows`);
    } catch (err) {
      notify(errorMessage(err), "error");
    } finally {
      setIsDownloading(false);
    }
  };

  if (!active) return null;
  if (isLoading) {
    return (
      <div className="version-subtitle">
        Reading billing for every workspace
        {progress.total ? ` — ${progress.done} of ${progress.total}` : ""}. It
        takes a minute or two...
      </div>
    );
  }
  if (error || !report) {
    return (
      <div className="data-cleanup-toolbar">
        <span className="data-cleanup-bad">{error || "No report."}</span>
        <button
          className="version-save-btn data-cleanup-secondary-btn"
          onClick={load}
        >
          Try again
        </button>
      </div>
    );
  }

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const shown = rows.slice(offset, offset + PAGE_SIZE);

  return (
    <>
      <div className="data-cleanup-toolbar data-cleanup-report-meta">
        <span className="version-subtitle">
          As of {formatDateTime(report.generatedAt.toISOString())} · Workspaces:{" "}
          {counts.workspace.DELETE} delete, {counts.workspace.KEEP} keep ·
          Users: {counts.user.DELETE} delete, {counts.user.KEEP} keep
        </span>
        <button
          className="version-save-btn data-cleanup-secondary-btn"
          onClick={load}
          title="Work the report out again, now."
        >
          Refresh
        </button>
      </div>

      <div className="data-cleanup-toolbar">
        <div className="data-cleanup-filters">
          <select
            className="version-input data-cleanup-select"
            aria-label="Show"
            value={type}
            onChange={(e) => changeFilter(() => setType(e.target.value))}
          >
            <option value="">Workspaces and users</option>
            <option value="workspace">Only workspaces</option>
            <option value="user">Only users</option>
          </select>
          <select
            className="version-input data-cleanup-select"
            aria-label="Action"
            value={action}
            onChange={(e) => changeFilter(() => setAction(e.target.value))}
          >
            <option value="">Delete and keep</option>
            <option value="DELETE">Only delete</option>
            <option value="KEEP">Only keep</option>
          </select>
          <input
            className="version-input data-cleanup-search"
            value={search}
            placeholder="Search name, email or id"
            aria-label="Search"
            onChange={(e) => changeFilter(() => setSearch(e.target.value))}
          />
        </div>
        <div className="data-cleanup-actions">
          <span className="version-subtitle">{rows.length} rows</span>
          <button
            className="version-save-btn data-cleanup-secondary-btn"
            onClick={handleDownload}
            disabled={!rows.length || isDownloading}
            title="Download these rows — every page — with these columns, as an Excel file."
          >
            {isDownloading ? "Downloading..." : "Download Excel"}
          </button>
        </div>
      </div>

      {shown.length === 0 ? (
        <div className="version-subtitle">Nothing here.</div>
      ) : (
        <div className="data-cleanup-table-scroll">
          <table className="data-cleanup-table data-cleanup-report-table">
            <thead>
              <tr>
                {COLUMNS.map((c) => (
                  <th key={c.header}>{c.header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={`${r.type}:${r.workspaceId || r.ownerId}`}>
                  {COLUMNS.map((c) => (
                    <td
                      key={c.header}
                      className={
                        c.header === "reason"
                          ? "data-cleanup-reason"
                          : c.header === "action"
                            ? `data-cleanup-action-${r.action.toLowerCase()}`
                            : undefined
                      }
                    >
                      {show(c.get(r))}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows.length > PAGE_SIZE && (
        <div className="data-cleanup-pagination">
          <button
            className="version-save-btn data-cleanup-secondary-btn"
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            disabled={offset === 0}
          >
            Previous
          </button>
          <span className="version-subtitle">
            Page {page} of {pages}
          </span>
          <button
            className="version-save-btn data-cleanup-secondary-btn"
            onClick={() => setOffset(offset + PAGE_SIZE)}
            disabled={page >= pages}
          >
            Next
          </button>
        </div>
      )}
    </>
  );
}
